import { IPutPromoDeductionAppliedParams } from "@/core/repositories/promotion";
import { GetPromoDeductionMatchSummaryUseCase, PutPromoDeductionAppliedUseCase } from "@/core/usecases/promotion";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { PromotionRepository } from "@/infrastructure/repositories/promotion/Promotion.repository";
import { atom } from "jotai";
import { promoMatchByDeductionIdAtom, PromoMatchDeductionState } from "./promoMatch.atoms";
import { deductionAtom, deductionsAtom } from "../deduction/deduction";

const client = GraphQlAdapter.getInstance().getClient();
const promotionRepository = new PromotionRepository(client);

const emptyState: PromoMatchDeductionState = {
  summaries: [],
  isLoading: false,
  isApplying: false,
  error: null,
};

export interface FetchPromoMatchSummariesParams {
  deductionId: number;
  /** When true, bypasses Apollo cache (e.g. after eviction). Default uses cache-first. */
  refetch?: boolean;
}

export const fetchPromoMatchSummariesAtom = atom(
  null,
  async (get, set, { deductionId, refetch = false }: FetchPromoMatchSummariesParams) => {
    const current = get(promoMatchByDeductionIdAtom)[deductionId] ?? emptyState;
    set(promoMatchByDeductionIdAtom, {
      ...get(promoMatchByDeductionIdAtom),
      [deductionId]: { ...current, isLoading: true, error: null },
    });

    try {
      const summaries = await new GetPromoDeductionMatchSummaryUseCase(promotionRepository).execute(
        { deductionId },
        refetch
      );
      set(promoMatchByDeductionIdAtom, {
        ...get(promoMatchByDeductionIdAtom),
        [deductionId]: { summaries, isLoading: false, isApplying: false, error: null },
      });
      return summaries;
    } catch (error) {
      set(promoMatchByDeductionIdAtom, {
        ...get(promoMatchByDeductionIdAtom),
        [deductionId]: { summaries: [], isLoading: false, isApplying: false, error: error as Error },
      });
      throw error;
    }
  }
);

export const putPromoDeductionAppliedAtom = atom(
  null,
  async (get, set, params: IPutPromoDeductionAppliedParams) => {
    const { deductionId } = params;
    const current = get(promoMatchByDeductionIdAtom)[deductionId] ?? emptyState;
    set(promoMatchByDeductionIdAtom, {
      ...get(promoMatchByDeductionIdAtom),
      [deductionId]: { ...current, isApplying: true, error: null },
    });

    try {
      const { summaries, deduction } = await new PutPromoDeductionAppliedUseCase(promotionRepository).execute(params);
      const deductions = get(deductionsAtom);
      const newDeductions = deductions.map(item => (item.id === deduction.id ? deduction : item));
      set(deductionAtom, {
        ...get(deductionAtom),
        deductions: newDeductions,
        isLoading: false,
        error: null,
      });

      set(promoMatchByDeductionIdAtom, {
        ...get(promoMatchByDeductionIdAtom),
        [deductionId]: { summaries, isLoading: false, isApplying: false, error: null },
      });
      return summaries;
    } catch (error) {
      const failedState = get(promoMatchByDeductionIdAtom)[deductionId] ?? emptyState;
      set(promoMatchByDeductionIdAtom, {
        ...get(promoMatchByDeductionIdAtom),
        [deductionId]: { ...failedState, isApplying: false, error: error as Error },
      });
      throw error;
    }
  }
);
